import React, { useState, useEffect, useRef } from 'react';
import { 
  HelpCircle, 
  X, 
  Search, 
  Book, 
  Keyboard, 
  MessageCircle,
  ChevronRight,
  ExternalLink,
  Lightbulb,
  Building2,
  BarChart3,
  Settings,
  Tag,
  Layout,
  FileText,
  Mail,
  Users,
  Video,
  TrendingUp,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Zap,
  Shield
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

// Help content organized by sections
const helpContent = {
  dashboard: {
    title: 'Dashboard Overview',
    icon: BarChart3,
    description: 'Your central hub for platform metrics and quick actions.',
    guides: [
      {
        title: 'Understanding Your Metrics',
        content: 'The dashboard displays key platform statistics including total clinics, active patients, revenue metrics, and recent activity. Use these insights to monitor platform health.'
      },
      {
        title: 'Quick Actions',
        content: 'Access frequently used features directly from the dashboard cards. Click on any metric card to navigate to its detailed section.'
      }
    ],
    tips: [
      'Check the dashboard daily to monitor new clinic registrations',
      'Revenue trends help identify growth patterns',
      'Use the activity feed to track recent changes'
    ]
  },
  onboarding: {
    title: 'Onboarding Flows',
    icon: Users,
    description: 'Step-by-step guides for all platform onboarding processes.',
    guides: [
      {
        title: '1. Demo Request Flow (Public)',
        content: 'Step 1: Visit the homepage → Step 2: Click "Schedule a Demo" button → Step 3: Fill in the form (Name, Email, Phone, Company, Preferred Time, Message) → Step 4: Click "Schedule My Demo" → Step 5: Receive confirmation message. Demo requests appear in Super Admin Dashboard → Demo Requests section for follow-up.'
      },
      {
        title: '2. Trial Signup Flow (Public)',
        content: 'Step 1: Click "Start Free 30-Day Trial" on homepage → Step 2: Fill Step 1 (Clinic Name, Full Name, Email, Password) → Step 3: Click "Continue" → Step 4: Fill Step 2 (Phone, Address) → Step 5: Click "Review & Finish" → Step 6: Review information and confirm → Step 7: Account created with 30-day trial. No credit card required.'
      },
      {
        title: '3. Clinic Invitation Flow',
        content: 'Step 1: Login as Super Admin → Step 2: Go to Clinics section → Step 3: Click "Invite Clinic" tab → Step 4: Fill clinic details (Name, Subdomain, Admin Email, Admin Name, Phone, Address) → Step 5: Select trial duration → Step 6: Click "Send Invitation" → Step 7: Clinic admin receives email invitation → Step 8: Admin clicks link and completes registration.'
      },
      {
        title: '4. Staff Invitation Flow',
        content: 'Step 1: Login as Clinic Admin → Step 2: Go to Settings → Staff Management → Step 3: Click "Invite Staff" → Step 4: Enter staff email and select role → Step 5: Click "Send Invitation" → Step 6: Staff member receives email → Step 7: Staff clicks link and creates account → Step 8: Staff can now access clinic dashboard.'
      },
      {
        title: '5. Member/Patient Creation',
        content: 'Step 1: Login as Clinic Admin/Staff → Step 2: Go to Members section → Step 3: Click "Add Member" → Step 4: Fill member details (Name, Email, Phone, DOB, Address) → Step 5: Select membership plan (optional) → Step 6: Click "Create Member" → Step 7: Member receives welcome email → Step 8: Digital membership card is generated.'
      }
    ],
    tips: [
      'Demo requests should be followed up within 24 hours',
      'Trial clinics have 30 days to explore all features',
      'Invited clinic admins can immediately start setting up their practice',
      'Staff roles determine access levels within the clinic',
      'Members can view their digital card from their profile'
    ]
  },
  clinics: {
    title: 'Clinic Management',
    icon: Building2,
    description: 'Manage all registered clinics on the platform.',
    guides: [
      {
        title: 'Adding a New Clinic',
        content: 'Click "Add Clinic" to create a new clinic. Fill in the required information including clinic name, contact details, and subscription tier. An invitation will be sent to the clinic admin.'
      },
      {
        title: 'Managing Clinic Status',
        content: 'Toggle clinic status between Active, Suspended, and Inactive. Suspended clinics retain their data but cannot access the platform. Inactive clinics are archived.'
      },
      {
        title: 'Subscription Management',
        content: 'Assign or change subscription tiers for clinics. Higher tiers unlock additional features like advanced analytics, more staff accounts, and priority support.'
      }
    ],
    tips: [
      'Use filters to quickly find specific clinics',
      'Export clinic data for reporting purposes',
      'Set up trial periods for new clinics'
    ]
  },
  pricing: {
    title: 'Pricing Tiers',
    icon: Tag,
    description: 'Configure subscription plans and pricing.',
    guides: [
      {
        title: 'Creating Pricing Tiers',
        content: 'Define subscription tiers with custom pricing, feature limits, and billing cycles. Each tier can have different patient limits, staff accounts, and feature access.'
      },
      {
        title: 'Feature Configuration',
        content: 'Enable or disable specific features for each tier. Common features include appointment scheduling, messaging, reports, and integrations.'
      }
    ],
    tips: [
      'Offer a free trial tier to attract new clinics',
      'Create annual pricing for better retention',
      'Highlight most popular tier to guide decisions'
    ]
  },
  analytics: {
    title: 'Platform Analytics',
    icon: TrendingUp,
    description: 'Deep insights into platform performance and usage.',
    guides: [
      {
        title: 'Revenue Analytics',
        content: 'Track monthly recurring revenue (MRR), annual recurring revenue (ARR), and growth trends. Identify top-performing clinics and revenue sources.'
      },
      {
        title: 'Usage Metrics',
        content: 'Monitor active users, feature adoption rates, and engagement metrics. Use these insights to improve the platform and identify training needs.'
      }
    ],
    tips: [
      'Set up automated weekly reports',
      'Compare month-over-month metrics',
      'Export data for executive presentations'
    ]
  },
  homepage: {
    title: 'Homepage CMS',
    icon: Layout,
    description: 'Customize the public landing page content.',
    guides: [
      {
        title: 'Editing Hero Section',
        content: 'Update the main headline, description, and call-to-action buttons. Changes are saved automatically and reflected on the live site immediately.'
      },
      {
        title: 'Managing Partner Logos',
        content: 'Add, remove, or reorder partner/client logos in the "Trusted By" section. Upload logos in PNG or SVG format for best quality.'
      },
      {
        title: 'Feature Highlights',
        content: 'Edit the features section to showcase platform capabilities. Each feature can have an icon, title, and description.'
      }
    ],
    tips: [
      'Preview changes before publishing',
      'Use high-resolution images for best display',
      'Keep headlines concise and action-oriented'
    ]
  },
  pages: {
    title: 'CMS Pages',
    icon: FileText,
    description: 'Create and manage content pages with the rich text editor.',
    guides: [
      {
        title: 'Creating a New Page',
        content: 'Click "New Page" to create content. Use the rich text editor to format text, add images, and create structured content. Set the URL slug for SEO-friendly addresses.'
      },
      {
        title: 'Using the Rich Text Editor',
        content: 'The editor supports headings, bold, italic, lists, links, images, code blocks, and more. Use the toolbar or keyboard shortcuts for quick formatting.'
      },
      {
        title: 'Publishing Pages',
        content: 'Save pages as drafts or publish them immediately. Published pages are accessible via their URL slug. Unpublish to hide without deleting.'
      }
    ],
    tips: [
      'Use descriptive URL slugs for better SEO',
      'Add meta descriptions for search engines',
      'Preview pages before publishing'
    ]
  },
  content: {
    title: 'Content Pages',
    icon: FileText,
    description: 'Manage static content and legal pages.',
    guides: [
      {
        title: 'Managing Legal Pages',
        content: 'Update Terms of Service, Privacy Policy, and other legal content. These pages are linked from the footer and registration flows.'
      }
    ],
    tips: [
      'Review legal pages quarterly',
      'Keep a changelog of policy updates',
      'Notify users of significant changes'
    ]
  },
  demos: {
    title: 'Demo Requests',
    icon: Video,
    description: 'Manage incoming demo and trial requests.',
    guides: [
      {
        title: 'Processing Demo Requests',
        content: 'Review incoming demo requests, mark them as contacted, scheduled, or completed. Add notes to track conversation history.'
      },
      {
        title: 'Converting to Clinics',
        content: 'When a demo leads to signup, convert the request directly to a new clinic account. Previous conversation history is preserved.'
      }
    ],
    tips: [
      'Respond to demos within 24 hours',
      'Use templates for common responses',
      'Track conversion rates from demos'
    ]
  },
  email: {
    title: 'Email Configuration',
    icon: Mail,
    description: 'Configure email delivery settings.',
    guides: [
      {
        title: 'SendGrid Setup',
        content: 'Enter your SendGrid API key and configure sender details. Test the connection to verify emails are sending correctly.'
      },
      {
        title: 'SMTP Configuration',
        content: 'Alternatively, use custom SMTP settings. Provide host, port, username, and password. TLS is recommended for security.'
      },
      {
        title: 'Testing Email Delivery',
        content: 'Use the test email feature to send a sample message. Check spam folders if test emails don\'t arrive.'
      }
    ],
    tips: [
      'Use a dedicated sending domain',
      'Set up SPF and DKIM records',
      'Monitor email delivery rates'
    ]
  },
  admins: {
    title: 'Super Admin Management',
    icon: Users,
    description: 'Manage platform administrator accounts and roles.',
    guides: [
      {
        title: 'Admin Roles',
        content: 'Platform Admin: Full access to all features. Support Admin: Can manage clinics and view data. Content Editor: Can only edit CMS content.'
      },
      {
        title: 'Creating New Admins',
        content: 'Add new administrators by providing their email and assigning a role. They will receive an invitation to set up their account.'
      },
      {
        title: 'Security Best Practices',
        content: 'Require 2FA for all admin accounts. Review admin activity logs regularly. Remove access promptly when admins leave.'
      }
    ],
    tips: [
      'Follow principle of least privilege',
      'Audit admin access quarterly',
      'Use strong, unique passwords'
    ]
  },
  settings: {
    title: 'Platform Settings',
    icon: Settings,
    description: 'Configure global platform settings and preferences.',
    guides: [
      {
        title: 'General Settings',
        content: 'Configure platform name, default timezone, and contact information. These settings apply globally across all clinics.'
      },
      {
        title: 'Security Settings',
        content: 'Enable or require 2FA, set password policies, and configure session timeouts. Higher security settings protect sensitive health data.'
      }
    ],
    tips: [
      'Review settings after major updates',
      'Document any custom configurations',
      'Test changes in staging first'
    ]
  },
  security: {
    title: 'Security Management',
    icon: Shield,
    description: 'Manage account lockouts, security policies, and monitoring.',
    guides: [
      {
        title: 'Account Lockout Policy',
        content: 'Step 1: Go to Security section in sidebar → Step 2: Click "Policy" tab → Step 3: Set "Max Failed Attempts" (default: 5) → Step 4: Set "Lockout Duration" in minutes (default: 15) → Step 5: Click "Save Policy". Accounts are automatically locked after exceeding failed attempts.'
      },
      {
        title: 'Managing Locked Accounts',
        content: 'Step 1: Go to Security → Lockouts tab → Step 2: View list of locked accounts with details → Step 3: Click "Unlock" to manually unlock an account → Step 4: Optionally click "Reset Password" to force password change on next login.'
      },
      {
        title: 'Security Alerts',
        content: 'Step 1: Go to Security → Alerts tab → Step 2: View active security alerts → Step 3: Click "Resolve" to mark as handled → Step 4: Click "Scan for Threats" to manually check for suspicious activity. Alerts are automatically generated for multiple failed login attempts from same IP.'
      },
      {
        title: 'IP Blacklist Management',
        content: 'Step 1: Go to Security → IP Blacklist tab → Step 2: Enter IP address to block → Step 3: Enter reason for blocking → Step 4: Click "Block IP" → Step 5: Blocked IPs cannot access the platform. To remove, click "Remove" next to the IP.'
      },
      {
        title: 'Login History',
        content: 'Step 1: Go to Security → History tab → Step 2: View all login attempts (successful and failed) → Step 3: Use search to filter by email → Step 4: Use dropdown to filter by success/failed. Includes timestamp, IP address, and device information.'
      }
    ],
    tips: [
      'Review security alerts daily',
      'Block IPs with repeated failed login attempts',
      'Enable 2FA for all admin accounts',
      'Set session timeout to auto-logout inactive users',
      'Check login history for unusual patterns'
    ]
  },
  billing: {
    title: 'Billing & Payment Gateways',
    icon: Tag,
    description: 'Configure payment processors and billing settings.',
    guides: [
      {
        title: 'Stripe Setup',
        content: 'Step 1: Go to Settings → Billing tab → Step 2: Find Stripe section → Step 3: Enter Publishable Key (pk_...) → Step 4: Enter Secret Key (sk_...) → Step 5: Enter Webhook Secret (whsec_...) → Step 6: Toggle "Test Mode" for testing → Step 7: Toggle "Enable Stripe" → Step 8: Click "Save Stripe Configuration". Get keys from dashboard.stripe.com/apikeys.'
      },
      {
        title: 'PayPal Setup',
        content: 'Step 1: Go to Settings → Billing tab → Step 2: Find PayPal section → Step 3: Enter Client ID → Step 4: Enter Client Secret → Step 5: Toggle "Sandbox Mode" for testing → Step 6: Toggle "Enable PayPal" → Step 7: Click "Save PayPal Configuration". Get credentials from developer.paypal.com/dashboard/applications.'
      },
      {
        title: 'Square Setup',
        content: 'Step 1: Go to Settings → Billing tab → Step 2: Find Square section → Step 3: Enter Application ID → Step 4: Enter Access Token → Step 5: Enter Location ID → Step 6: Toggle "Sandbox Mode" for testing → Step 7: Toggle "Enable Square" → Step 8: Click "Save Square Configuration". Get credentials from developer.squareup.com/apps.'
      },
      {
        title: 'Trial Period Settings',
        content: 'Step 1: Scroll to Trial Period Settings section → Step 2: Set "Default Trial Duration" (e.g., 30 days) → Step 3: Set "Grace Period" after trial ends → Step 4: Toggle "Auto-start trial for new clinics" → Step 5: Click "Save Trial Settings".'
      },
      {
        title: 'Payment & Invoice Settings',
        content: 'Step 1: Set currency (USD, EUR, GBP, etc.) → Step 2: Set tax rate percentage → Step 3: Set failed payment retry attempts → Step 4: Set invoice prefix (e.g., "INV-") → Step 5: Set payment terms in days → Step 6: Toggle auto-generate invoices → Step 7: Save all settings.'
      }
    ],
    tips: [
      'Always test with sandbox/test mode first',
      'Keep API keys secure and never share publicly',
      'Set up webhook endpoints for payment notifications',
      'Configure SPF/DKIM for payment receipt emails',
      'Review payment settings before going live'
    ]
  }
};

// Keyboard shortcuts
const keyboardShortcuts = [
  { keys: ['Ctrl', 'K'], action: 'Open search' },
  { keys: ['Ctrl', 'H'], action: 'Toggle help panel' },
  { keys: ['Esc'], action: 'Close modal/panel' },
  { keys: ['Ctrl', 'S'], action: 'Save changes' },
  { keys: ['Ctrl', 'N'], action: 'Create new item' },
  { keys: ['?'], action: 'Show keyboard shortcuts' }
];

// FAQs
const faqs = [
  {
    question: 'How do I add a new clinic?',
    answer: 'Navigate to the Clinics section and click "Add Clinic". Fill in the required information and select a subscription tier. The clinic admin will receive an invitation email.'
  },
  {
    question: 'How do I change a clinic\'s subscription?',
    answer: 'Go to Clinics, find the clinic, and click Edit. Select a new pricing tier from the dropdown and save changes. The clinic will be notified of the change.'
  },
  {
    question: 'How do I reset a clinic admin\'s password?',
    answer: 'You cannot directly reset passwords. Direct the clinic admin to use the "Forgot Password" link on the login page, which will send a secure reset email.'
  },
  {
    question: 'How do I export platform data?',
    answer: 'Go to Platform Analytics and use the Export feature. Select the date range and data types you want to include. Reports are generated in CSV or PDF format.'
  },
  {
    question: 'How do I configure email notifications?',
    answer: 'Navigate to Email settings and choose between SendGrid or SMTP. Enter your credentials and test the connection before saving.'
  },
  {
    question: 'What do the different admin roles do?',
    answer: 'Platform Admin has full access. Support Admin can manage clinics and users. Content Editor can only manage CMS pages and content.'
  },
  {
    question: 'How does the trial signup process work?',
    answer: 'Users click "Start Free 30-Day Trial" on the homepage, complete a 3-step form (clinic info, contact details, review), and their account is created instantly. No credit card required.'
  },
  {
    question: 'How do I invite a new clinic to the platform?',
    answer: 'Go to Clinics → Invitations tab → Click "Invite Clinic" → Fill in clinic name, subdomain, admin details → Select trial duration → Click Send. The admin receives an email to complete registration.'
  },
  {
    question: 'How do I manage demo requests?',
    answer: 'Go to Demo Requests section. View pending requests, mark them as contacted/completed, add notes, and optionally convert successful demos to clinic accounts.'
  },
  {
    question: 'How do I set up payment gateways?',
    answer: 'Go to Settings → Billing tab. Enter your API credentials for Stripe, PayPal, or Square. Toggle test/sandbox mode for testing, then enable the gateway. Always test before going live.'
  },
  {
    question: 'How do I unlock a locked account?',
    answer: 'Go to Security → Lockouts tab. Find the locked account and click "Unlock". Optionally force a password reset for security.'
  },
  {
    question: 'How do I block a suspicious IP address?',
    answer: 'Go to Security → IP Blacklist tab. Enter the IP address and reason, then click "Block IP". The IP will be immediately blocked from accessing the platform.'
  }
];

const HelpSystem = ({ activeSection = 'dashboard' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('main'); // main, section, shortcuts, faq, search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);

  // Get current section help content
  const currentHelp = helpContent[activeSection] || helpContent.dashboard;
  const SectionIcon = currentHelp.icon;

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        // Don't close if clicking the help button
        if (event.target.closest('[data-help-trigger]')) return;
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Keyboard shortcut to toggle help
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'h') {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.target.matches('input, textarea')) {
        e.preventDefault();
        setIsOpen(true);
        setView('shortcuts');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus search input when opening search view
  useEffect(() => {
    if (view === 'search' && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [view]);

  // Search functionality
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const results = [];
    const lowerQuery = query.toLowerCase();

    // Search in help content
    Object.entries(helpContent).forEach(([key, section]) => {
      // Check title and description
      if (section.title.toLowerCase().includes(lowerQuery) ||
          section.description.toLowerCase().includes(lowerQuery)) {
        results.push({
          type: 'section',
          key,
          title: section.title,
          description: section.description,
          icon: section.icon
        });
      }

      // Check guides
      section.guides?.forEach((guide, idx) => {
        if (guide.title.toLowerCase().includes(lowerQuery) ||
            guide.content.toLowerCase().includes(lowerQuery)) {
          results.push({
            type: 'guide',
            sectionKey: key,
            sectionTitle: section.title,
            title: guide.title,
            content: guide.content
          });
        }
      });
    });

    // Search in FAQs
    faqs.forEach((faq, idx) => {
      if (faq.question.toLowerCase().includes(lowerQuery) ||
          faq.answer.toLowerCase().includes(lowerQuery)) {
        results.push({
          type: 'faq',
          index: idx,
          question: faq.question,
          answer: faq.answer
        });
      }
    });

    setSearchResults(results.slice(0, 10)); // Limit results
  };

  const renderMainView = () => (
    <div className="space-y-4">
      {/* Contextual Help - Current Section */}
      <div 
        className="p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setView('section')}
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-teal-600 rounded-lg">
            <SectionIcon className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{currentHelp.title}</h3>
            <p className="text-sm text-gray-600">{currentHelp.description}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </div>
        <Badge variant="secondary" className="bg-teal-200 text-teal-800">
          Current Section
        </Badge>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setView('search')}
          className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
        >
          <Search className="h-5 w-5 text-gray-500" />
          <span className="text-sm font-medium">Search Help</span>
        </button>
        <button
          onClick={() => setView('faq')}
          className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
        >
          <MessageCircle className="h-5 w-5 text-gray-500" />
          <span className="text-sm font-medium">FAQs</span>
        </button>
        <button
          onClick={() => setView('shortcuts')}
          className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
        >
          <Keyboard className="h-5 w-5 text-gray-500" />
          <span className="text-sm font-medium">Shortcuts</span>
        </button>
        <button
          onClick={() => setView('all')}
          className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
        >
          <Book className="h-5 w-5 text-gray-500" />
          <span className="text-sm font-medium">All Topics</span>
        </button>
      </div>

      {/* Quick Tips */}
      {currentHelp.tips && currentHelp.tips.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium text-gray-700">Quick Tips</span>
          </div>
          <ul className="space-y-2">
            {currentHelp.tips.slice(0, 3).map((tip, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Contact Support */}
      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Need more help?</h4>
        <p className="text-sm text-gray-600 mb-3">
          Contact our support team for personalized assistance.
        </p>
        <a
          href="mailto:support@mymedmember.com"
          className="inline-flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
        >
          <Mail className="h-4 w-4" />
          support@mymedmember.com
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );

  const renderSectionView = () => (
    <div className="space-y-4">
      <button
        onClick={() => setView('main')}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Help
      </button>

      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-teal-600 rounded-lg">
          <SectionIcon className="h-6 w-6 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{currentHelp.title}</h2>
          <p className="text-sm text-gray-600">{currentHelp.description}</p>
        </div>
      </div>

      {/* Guides */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Guides</h3>
        {currentHelp.guides?.map((guide, idx) => (
          <div key={idx} className="p-4 bg-white border rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">{guide.title}</h4>
            <p className="text-sm text-gray-600">{guide.content}</p>
          </div>
        ))}
      </div>

      {/* Tips */}
      {currentHelp.tips && currentHelp.tips.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Pro Tips</h3>
          <ul className="space-y-2">
            {currentHelp.tips.map((tip, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                <Zap className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const renderSearchView = () => (
    <div className="space-y-4">
      <button
        onClick={() => { setView('main'); setSearchQuery(''); setSearchResults([]); }}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Help
      </button>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={searchInputRef}
          type="text"
          placeholder="Search help articles..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {searchResults.length > 0 ? (
        <div className="space-y-2">
          {searchResults.map((result, idx) => (
            <div 
              key={idx}
              className="p-3 bg-white border rounded-lg hover:border-teal-300 cursor-pointer transition-colors"
              onClick={() => {
                if (result.type === 'faq') {
                  setSelectedFaq(result.index);
                  setView('faq');
                }
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                {result.type === 'section' && <result.icon className="h-4 w-4 text-teal-600" />}
                {result.type === 'guide' && <Book className="h-4 w-4 text-blue-600" />}
                {result.type === 'faq' && <MessageCircle className="h-4 w-4 text-purple-600" />}
                <span className="text-xs text-gray-500 uppercase">
                  {result.type === 'section' ? 'Section' : result.type === 'guide' ? result.sectionTitle : 'FAQ'}
                </span>
              </div>
              <h4 className="font-medium text-gray-900 text-sm">
                {result.title || result.question}
              </h4>
              <p className="text-xs text-gray-500 line-clamp-2 mt-1">
                {result.description || result.content || result.answer}
              </p>
            </div>
          ))}
        </div>
      ) : searchQuery ? (
        <div className="text-center py-8 text-gray-500">
          <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No results found for &ldquo;{searchQuery}&rdquo;</p>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <p>Start typing to search...</p>
        </div>
      )}
    </div>
  );

  const renderShortcutsView = () => (
    <div className="space-y-4">
      <button
        onClick={() => setView('main')}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Help
      </button>

      <div className="flex items-center gap-2 mb-4">
        <Keyboard className="h-5 w-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">Keyboard Shortcuts</h2>
      </div>

      <div className="space-y-2">
        {keyboardShortcuts.map((shortcut, idx) => (
          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-700">{shortcut.action}</span>
            <div className="flex items-center gap-1">
              {shortcut.keys.map((key, keyIdx) => (
                <React.Fragment key={keyIdx}>
                  <kbd className="px-2 py-1 bg-white border rounded text-xs font-mono shadow-sm">
                    {key}
                  </kbd>
                  {keyIdx < shortcut.keys.length - 1 && (
                    <span className="text-gray-400 text-xs">+</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderFaqView = () => (
    <div className="space-y-4">
      <button
        onClick={() => { setView('main'); setSelectedFaq(null); }}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Help
      </button>

      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="h-5 w-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">Frequently Asked Questions</h2>
      </div>

      <div className="space-y-2">
        {faqs.map((faq, idx) => (
          <div 
            key={idx}
            className={`border rounded-lg overflow-hidden transition-all ${
              selectedFaq === idx ? 'border-teal-300 bg-teal-50' : 'bg-white hover:border-gray-300'
            }`}
          >
            <button
              onClick={() => setSelectedFaq(selectedFaq === idx ? null : idx)}
              className="w-full flex items-center justify-between p-4 text-left"
            >
              <span className="font-medium text-gray-900 text-sm">{faq.question}</span>
              <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${
                selectedFaq === idx ? 'rotate-90' : ''
              }`} />
            </button>
            {selectedFaq === idx && (
              <div className="px-4 pb-4">
                <p className="text-sm text-gray-600">{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderAllTopicsView = () => (
    <div className="space-y-4">
      <button
        onClick={() => setView('main')}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Help
      </button>

      <div className="flex items-center gap-2 mb-4">
        <Book className="h-5 w-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">All Help Topics</h2>
      </div>

      <div className="space-y-2">
        {Object.entries(helpContent).map(([key, section]) => {
          const Icon = section.icon;
          return (
            <button
              key={key}
              onClick={() => {
                // Navigate to this section's help
                setView('section');
              }}
              className="w-full flex items-center gap-3 p-3 bg-white border rounded-lg hover:border-teal-300 transition-colors text-left"
            >
              <div className="p-2 bg-gray-100 rounded-lg">
                <Icon className="h-4 w-4 text-gray-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 text-sm">{section.title}</h4>
                <p className="text-xs text-gray-500 line-clamp-1">{section.description}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      {/* Floating Help Button */}
      <button
        data-help-trigger
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-lg transition-all duration-300 ${
          isOpen 
            ? 'bg-gray-700 hover:bg-gray-800' 
            : 'bg-teal-600 hover:bg-teal-700 hover:scale-110'
        }`}
        title="Help (Ctrl+H)"
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <HelpCircle className="h-6 w-6 text-white" />
        )}
      </button>

      {/* Help Panel */}
      <div
        ref={panelRef}
        className={`fixed bottom-24 right-6 z-50 w-96 max-h-[calc(100vh-150px)] bg-white rounded-2xl shadow-2xl transition-all duration-300 overflow-hidden ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Shield className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-semibold">Help Center</h2>
              <p className="text-sm text-teal-100">Super Admin Dashboard</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(100vh-280px)]">
          {view === 'main' && renderMainView()}
          {view === 'section' && renderSectionView()}
          {view === 'search' && renderSearchView()}
          {view === 'shortcuts' && renderShortcutsView()}
          {view === 'faq' && renderFaqView()}
          {view === 'all' && renderAllTopicsView()}
        </div>

        {/* Footer */}
        <div className="border-t px-4 py-3 bg-gray-50">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Press <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Ctrl</kbd> + <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">H</kbd> to toggle</span>
            <span>v1.0</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpSystem;
